import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppContext } from 'providers/AppProvider';
import { useUserContext } from 'providers/UserProvider';

import FalconCloseButton from 'components/common/FalconCloseButton';

import { searchApi } from 'api';
import paths from 'routes/paths';

// SearchTypes moved inside component to access tenant and warehouse

const SearchInput = () => {
  const {
    config: { isDark },
  } = useAppContext();

  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const navigate = useNavigate();

  // SearchTypes defined inside component to access tenant and warehouse
  const SearchTypes = useMemo(
    () => [
      {
        label: 'Order',
        queryParam: 'channel_order_id',
        endpoint: 'orders',
        getUrl: id =>
          `${paths.orders.details(tenant, warehouse, id)}?tab=order-items`,
      },
      {
        label: 'Shipment',
        queryParam: 'shipment',
        endpoint: 'orders',
        getUrl: id =>
          `${paths.orders.details(tenant, warehouse, id)}?tab=shipments`,
      },
      {
        label: 'Invoice',
        queryParam: 'invoice_code',
        endpoint: 'orders',
        getUrl: id =>
          `${paths.orders.details(tenant, warehouse, id)}?tab=invoices`,
      },
      {
        label: 'Product',
        queryParam: 'product',
        endpoint: 'products',
        getUrl: id => `${paths.products.details(tenant, warehouse, id)}`,
      },
      {
        label: 'Manifest',
        queryParam: 'manifest_code',
        endpoint: 'manifests',
        getUrl: id => `${paths.manifests.details(tenant, warehouse, id)}`,
      },
    ],
    [tenant, warehouse],
  );

  const [searchTypeLabel, setSearchTypeLabel] = useState(SearchTypes[0].label);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchState, setSearchState] = useState({
    isLoading: false,
    results: [],
    showDropdown: false,
  });

  // 현재 선택된 SearchType 객체 가져오기
  const currentSearchType = useMemo(
    () => SearchTypes.find(t => t.label === searchTypeLabel),
    [SearchTypes, searchTypeLabel],
  );

  const handleNavigateToDetail = id => {
    setSearchTerm('');
    setSearchState({ isLoading: false, results: [], showDropdown: false });
    navigate(currentSearchType.getUrl(id));
  };

  const handleSearch = async e => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      setSearchState(prev => ({ ...prev, showDropdown: false }));
      return;
    }
    setSearchState(prev => ({ ...prev, isLoading: true }));

    try {
      const params = new URLSearchParams();
      params.append(currentSearchType.queryParam, searchTerm);

      let result;
      if (currentSearchType.endpoint === 'orders') {
        result = await searchApi.getSearchResultOfOrders(params);
      } else if (currentSearchType.endpoint === 'products') {
        result = await searchApi.getSearchResultOfProducts(params);
      } else if (currentSearchType.endpoint === 'manifests') {
        result = await searchApi.getSearchResultOfManifests(params);
      }

      if (result.length === 1) {
        handleNavigateToDetail(result[0].id);
      } else {
        setSearchState({
          isLoading: false,
          results: result,
          showDropdown: true,
        });
      }
    } catch (error) {
      setSearchState({ isLoading: false, results: [], showDropdown: true });
    }
  };

  const handleSearchTypeClick = typeLabel => {
    setSearchTypeLabel(typeLabel);
    setSearchState({ isLoading: false, results: [], showDropdown: false });
  };

  const handleSearchTermChange = e => {
    setSearchTerm(e.target.value);

    setSearchState({ isLoading: false, results: [], showDropdown: false });
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchState({ isLoading: false, results: [], showDropdown: false });
  };

  return (
    <Dropdown
      show={searchState.showDropdown && searchTerm.trim()}
      className="search-box"
    >
      <Dropdown.Toggle as="div" className="dropdown-caret-none">
        <Form className="position-relative" onSubmit={handleSearch}>
          <div
            className={`d-flex align-items-center ${
              !isDark ? 'bg-white' : ''
            } rounded-pill`}
          >
            <FontAwesomeIcon
              icon="search"
              className="position-absolute text-400 search-box-icon"
              style={{ left: '1rem', zIndex: 1 }}
            />

            <Dropdown as="div" className="ms-4 ps-2">
              <Dropdown.Toggle
                variant="link"
                className="text-decoration-none text-700 bg-transparent border-0 p-0"
                style={{ boxShadow: 'none' }}
              >
                {currentSearchType.label}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ zIndex: 1050 }}>
                {SearchTypes.map(type => (
                  <Dropdown.Item
                    key={type.label}
                    onClick={() => handleSearchTypeClick(type.label)}
                    active={searchTypeLabel === type.label}
                  >
                    {type.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Form.Control
              type="search"
              placeholder={`Search by ${currentSearchType.label.toLowerCase()}...`}
              className="rounded-pill search-input border-0 ps-2"
              value={searchTerm}
              onChange={handleSearchTermChange}
              readOnly={searchState.isLoading}
              style={{ boxShadow: 'none' }}
            />

            {searchTerm && (
              <div className="search-box-close-btn-container">
                <FalconCloseButton
                  size="sm"
                  noOutline
                  className="fs-11"
                  onClick={handleReset}
                />
              </div>
            )}
          </div>
        </Form>
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-100">
        <div className="scrollbar py-3" style={{ maxHeight: '24rem' }}>
          {searchTerm &&
            (searchState.results.length > 0 ? (
              searchState.results.map(result => (
                <Dropdown.Item
                  key={result.id}
                  onClick={() => handleNavigateToDetail(result.id)}
                  className="px-x1 py-2"
                >
                  {result.display_name}
                </Dropdown.Item>
              ))
            ) : (
              <div className="px-x1">
                <p className="fs-8 fw-bold text-center mb-0">
                  No results found
                </p>
              </div>
            ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SearchInput;
